import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import type { SignDocumentMetadata } from '../../store/signature-popup.store';

import { CrossIcon } from '../../../../../assets/icons/index';
import Button from '../../../../../components/button/index';
import Loader from '../../../../../components/loader/index';
import { ModalWrapper } from '../../../../../components/modal-wrapper/index';
import { QueryKey } from '../../../../../constants/index';
import { HttpException } from '../../../../../exceptions/index';
import { useLocale, useScreenSize } from '../../../../../hooks/index';
import { Company } from '../../../../../hooks/index';
import { SignaturePopupType } from '../../enums/signature-popup-type.const';
import { SignatureScopeFilter } from '../../enums/signature-scope.const';
import { SignatureStatus } from '../../enums/signature-status.const';
import {
  ensureDocuSignScriptLoaded,
  getDocuSignIntegrationKey,
} from '../../hooks/use-docusign-signing.hook';
import { useSignatureEmbeddedSessionSync } from '../../hooks/use-signature-embedded-session-sync.hook';
import { useSignatureRecipientView } from '../../hooks/use-signature-recipient-view.hook';
import { useSignatures } from '../../hooks/use-signatures.hook';
import { SignaturePopupStore } from '../../store/signature-popup.store';
import { SignatureSigningSuccess } from './signature-signing-success';

const MOUNT_POLL_MAX = 120;

const DOCUSIGN_READY_FALLBACK_MS = 8_000;
const PENDING_SIGNATURE_STATUSES = [
  SignatureStatus.SIGNATURE_PENDING,
  SignatureStatus.PARTIALLY_SIGNED,
];

const isDocuSignSigningCompleteSessionEnd = (event: unknown): boolean => {
  if (event === 'signing_complete') {
    return true;
  }
  if (typeof event === 'object' && event !== null && 'sessionEndType' in event) {
    return (event as { sessionEndType: unknown }).sessionEndType === 'signing_complete';
  }
  return false;
};

export const SignatureSigningModal: FC = () => {
  const { isMobile: _isMobile } = useScreenSize();
  const { messagesLocale } = useLocale();
  const { selectedCompanyId } = Company.useSelected();
  const { fetchRecipientViewUrl } = useSignatureRecipientView();
  const { syncAfterEmbeddedSession } = useSignatureEmbeddedSessionSync();
  const queryClient = useQueryClient();
  const isOpen = SignaturePopupStore(({ open }) => open);
  const type = SignaturePopupStore(({ type }) => type);
  const metadata = SignaturePopupStore(({ metadata }) => metadata);
  const handleDismiss = SignaturePopupStore(({ handleDismiss }) => handleDismiss);
  const openPopup = SignaturePopupStore(({ openPopup }) => openPopup);

  const mountRef = useRef<HTMLDivElement | null>(null);
  const hasTriggeredSyncRef = useRef(false);
  const [phase, setPhase] = useState<'error' | 'idle' | 'loading' | 'ready'>('idle');
  const [signingComplete, setSigningComplete] = useState(false);
  const currentSignatureId =
    type === SignaturePopupType.SIGN_DOCUMENT && metadata
      ? (metadata as SignDocumentMetadata).signatureId
      : '';

  const { signatures: pendingSignatures } = useSignatures({
    currentPage: 1,
    mySignatorySigned: false,
    scope: SignatureScopeFilter.MY_SIGNATURES,
    statuses: PENDING_SIGNATURE_STATUSES,
    take: 20,
    enabled: signingComplete && !!selectedCompanyId,
  });

  const nextPendingSignature = pendingSignatures.find(({ id }) => id !== currentSignatureId);

  const triggerEmbeddedSessionSync = useCallback(
    (signatureId: string) => {
      if (hasTriggeredSyncRef.current) {
        return;
      }
      hasTriggeredSyncRef.current = true;
      void (async () => {
        try {
          await syncAfterEmbeddedSession(signatureId);
        } catch {}
        void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SIGNATURES] });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENTS] });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDER] });
        void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDERS] });
      })();
    },
    [queryClient, syncAfterEmbeddedSession],
  );

  const handleCloseWithSync = () => {
    if (type === SignaturePopupType.SIGN_DOCUMENT && metadata) {
      const { signatureId } = metadata as SignDocumentMetadata;
      if (signatureId) {
        triggerEmbeddedSessionSync(signatureId);
      }
    }
    handleDismiss();
  };

  const handleSignNextDocument = () => {
    if (!nextPendingSignature) {
      return;
    }
    openPopup({
      metadata: {
        documentName: nextPendingSignature.name,
        signatureId: nextPendingSignature.id,
      },
      type: SignaturePopupType.SIGN_DOCUMENT,
    });
  };

  useEffect(() => {
    if (!isOpen || type !== SignaturePopupType.SIGN_DOCUMENT || !metadata) {
      setPhase('idle');
      setSigningComplete(false);
      return;
    }

    const meta = metadata as SignDocumentMetadata;
    if (!meta.signatureId) {
      return;
    }
    hasTriggeredSyncRef.current = false;
    setSigningComplete(false);

    const integrationKey = getDocuSignIntegrationKey();
    if (!integrationKey) {
      toast.error('Missing REACT_APP_DOCUSIGN_INTEGRATION_KEY');
      setPhase('error');
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let pollCount = 0;
    let mountNodeForCleanup: HTMLDivElement | null = null;
    let readyFallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let signingUiReady = false;

    const markSigningUiReady = () => {
      if (cancelled || signingUiReady) {
        return;
      }
      signingUiReady = true;
      if (readyFallbackTimer !== undefined) {
        clearTimeout(readyFallbackTimer);
        readyFallbackTimer = undefined;
      }
      setPhase('ready');
    };

    const runSigning = async () => {
      const el = mountRef.current;
      if (!el) {
        return;
      }

      mountNodeForCleanup = el;
      setPhase('loading');
      el.innerHTML = '';

      try {
        await ensureDocuSignScriptLoaded();
        const { url } = await fetchRecipientViewUrl(meta.signatureId);

        if (cancelled || !mountRef.current) {
          return;
        }

        const loader = window.DocuSign?.loadDocuSign;
        if (!loader) {
          throw new Error('DocuSign.loadDocuSign is not available');
        }

        const docusign = await loader(integrationKey);
        const signing = docusign.signing({
          displayFormat: 'focused',
          style: {
            branding: {
              primaryButton: {
                backgroundColor: '#2565C8',
                color: '#fff',
              },
            },
            signingNavigationButton: {
              finishText: messagesLocale['DONE'],
              position: 'bottom-center',
            },
          },
          url,
        });

        signing.on('ready', () => {
          markSigningUiReady();
        });

        signing.on('sessionEnd', (event) => {
          void (async () => {
            if (isDocuSignSigningCompleteSessionEnd(event)) {
              if (!cancelled) {
                // Session ended with submit -> show loading while backend sync runs.
                setPhase('loading');
              }
              if (readyFallbackTimer !== undefined) {
                clearTimeout(readyFallbackTimer);
                readyFallbackTimer = undefined;
              }
              signingUiReady = true;
              triggerEmbeddedSessionSync(meta.signatureId);
              if (!cancelled && mountRef.current) {
                mountRef.current.innerHTML = '';
              }
              if (!cancelled) {
                setPhase('ready');
                setSigningComplete(true);
              }
              return;
            }
            if (!cancelled) {
              handleDismiss();
            }
          })();
        });

        signing.mount(mountRef.current);
        readyFallbackTimer = setTimeout(() => {
          markSigningUiReady();
        }, DOCUSIGN_READY_FALLBACK_MS);
      } catch (e) {
        if (cancelled) {
          return;
        }
        setPhase('error');
        const msg =
          e instanceof HttpException
            ? String(e.body?.message ?? e.message)
            : e instanceof Error
              ? e.message
              : 'Signing failed';
        toast.error(msg);
      }
    };

    const waitForMount = () => {
      if (cancelled) {
        return;
      }
      const el = mountRef.current;
      if (el) {
        void runSigning();
        return;
      }
      pollCount += 1;
      if (pollCount > MOUNT_POLL_MAX) {
        setPhase('error');
        toast.error(messagesLocale['SIGN_DOCUMENT_LOAD_ERROR']);
        return;
      }
      rafId = requestAnimationFrame(waitForMount);
    };

    rafId = requestAnimationFrame(waitForMount);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (readyFallbackTimer !== undefined) {
        clearTimeout(readyFallbackTimer);
      }
      if (mountNodeForCleanup) {
        mountNodeForCleanup.innerHTML = '';
      }
      mountNodeForCleanup = null;
    };
  }, [
    fetchRecipientViewUrl,
    handleDismiss,
    isOpen,
    messagesLocale,
    metadata,
    queryClient,
    syncAfterEmbeddedSession,
    triggerEmbeddedSessionSync,
    type,
  ]);

  if (type !== SignaturePopupType.SIGN_DOCUMENT || !metadata) {
    return null;
  }

  const { documentName } = metadata as SignDocumentMetadata;

  return (
    <ModalWrapper
      className="!bottom-[25px] !left-[20px] !right-[20px] !top-[25px] !z-[61] !mx-auto flex !h-auto !w-auto !max-w-[990px] !translate-x-0 !translate-y-0 flex-col overflow-hidden rounded-xl bg-white shadow-lg"
      control={{
        open: isOpen,
        onOpenChange: (openState) => {
          if (!openState) {
            handleCloseWithSync();
          }
        },
      }}
      onBgClick={handleCloseWithSync}
      overlayClassName="z-[60]"
      side="CENTER"
    >
      <div className={twMerge('flex min-h-0 flex-1 flex-col', 'overflow-hidden')}>
        <div className="flex shrink-0 items-center justify-between px-6 py-4">
          <h2 className="min-w-0 truncate pr-2 text-[20px] font-medium text-gray-700">
            {documentName}
          </h2>
          <Button
            className="h-8 w-8 shrink-0 rounded-full border border-[#F2F2F2] p-0 shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
            onClick={handleCloseWithSync}
            styleType="NONE"
            type="button"
          >
            <CrossIcon className="h-4 w-4" iconColor="#344054" />
          </Button>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col bg-gray-25">
          {(phase === 'loading' || phase === 'idle') && !signingComplete && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
              <Loader className="h-24" />
            </div>
          )}
          {phase === 'error' ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center gap-4 px-4">
              <p className="text-center text-sm text-gray-600">
                {messagesLocale['SIGN_DOCUMENT_LOAD_ERROR']}
              </p>
              <Button onClick={handleCloseWithSync} styleType="BLUE_ROUNDED" type="button">
                {messagesLocale['CLOSE']}
              </Button>
            </div>
          ) : null}
          {signingComplete ? (
            <SignatureSigningSuccess
              canSignNextDocument={!!nextPendingSignature}
              onBackToMySignatures={handleCloseWithSync}
              onSignNextDocument={handleSignNextDocument}
            />
          ) : (
            <div
              className={twMerge(phase === 'error' ? 'hidden' : 'min-h-0 w-full flex-1')}
              ref={mountRef}
            />
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};
